/* EnhancedTokenPreview.tsx - Full Version (No Changes, as per Instructions) */
import React, { useState, useEffect, useRef } from "react";
import "./TokenPreview.css";
import "./PremiumStyles.css";
import SandclockAnimation from "./SandclockAnimation";

interface EnhancedTokenPreviewProps {
  name: string;
  symbol: string;
  description: string;
  imageUrl: string;
  maxSupply: string;
  decimals: number;
  feeBps: number;
  file: File | null;
}

const EnhancedTokenPreview: React.FC<EnhancedTokenPreviewProps> = ({
  name,
  symbol,
  description,
  imageUrl,
  maxSupply,
  decimals,
  feeBps,
  file,
}) => {
  const [previewImage, setPreviewImage] = useState<string>("");
  const [animatedDescription, setAnimatedDescription] = useState<string>("");
  const [curveProgress, setCurveProgress] = useState(0);
  const [formCompletionProgress, setFormCompletionProgress] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [glowIntensity, setGlowIntensity] = useState(0.4);
  const [rotateAngle, setRotateAngle] = useState(0);
  const tokenCardRef = useRef<HTMLDivElement>(null);

  // Handle file preview
  useEffect(() => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else if (imageUrl) {
      setPreviewImage(imageUrl);
    } else {
      setPreviewImage("");
    }
  }, [file, imageUrl]);

  // Calculate form completion progress for sandclock
  useEffect(() => {
    // Define all required fields with their weights
    const fields = [
      { value: name, weight: 20, required: true },
      { value: symbol, weight: 20, required: true },
      { value: description, weight: 20, required: true },
      { value: previewImage, weight: 20, required: true },
      { value: maxSupply, weight: 10, required: false },
      { value: decimals?.toString(), weight: 5, required: false },
      { value: feeBps?.toString(), weight: 5, required: false }
    ];

    // Calculate total possible weight (all fields)
    const totalWeight = fields.reduce((sum, field) => sum + field.weight, 0);

    // Calculate completed weight
    const completedWeight = fields.reduce((sum, field) => {
      const isCompleted = field.value && field.value.toString().trim();
      return sum + (isCompleted ? field.weight : 0);
    }, 0);

    // Calculate percentage
    const progress = Math.round((completedWeight / totalWeight) * 100);
    setFormCompletionProgress(progress);
  }, [name, symbol, description, previewImage, maxSupply, decimals, feeBps]);

  // Typewriter effect for description
  useEffect(() => {
    if (!description) {
      setAnimatedDescription("");
      return;
    }

    let currentIndex = 0;
    setAnimatedDescription("");

    const interval = setInterval(() => {
      if (currentIndex <= description.length) {
        setAnimatedDescription(description.substring(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [description]);

  // Animate bonding curve progress
  useEffect(() => {
    const interval = setInterval(() => {
      setCurveProgress((prev) => (prev + 0.5) % 100);
    }, 50);

    return () => clearInterval(interval);
  }, []);

  // Rotate glow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setRotateAngle((prev) => (prev + 1) % 360);
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Pulse glow effect
  useEffect(() => {
    const interval = setInterval(() => {
      setGlowIntensity((prev) => {
        const newValue = prev + (Math.random() * 0.1 - 0.05);
        return Math.max(0.3, Math.min(0.6, newValue)); // Keep between 0.3 and 0.6
      });
    }, 200);

    return () => clearInterval(interval);
  }, []);

  // Mouse move effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!tokenCardRef.current) return;

      const card = tokenCardRef.current;
      const rect = card.getBoundingClientRect();

      // Calculate mouse position relative to card center
      const cardCenterX = rect.left + rect.width / 2;
      const cardCenterY = rect.top + rect.height / 2;
      const mouseX = e.clientX - cardCenterX;
      const mouseY = e.clientY - cardCenterY;

      // Calculate tilt angles (limited range)
      const tiltX = (mouseY / (rect.height / 2)) * 4; // Max 4 degrees
      const tiltY = -(mouseX / (rect.width / 2)) * 4; // Max 4 degrees

      // Apply 3D transformation
      card.style.transform = `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale3d(1.02, 1.02, 1.02)`;

      // Dynamic light reflection
      const intensity = 0.2;
      const shine = document.createElement("div");
      shine.classList.add("dynamic-shine");
      shine.style.background = `radial-gradient(circle at ${e.clientX - rect.left
        }px ${e.clientY - rect.top
        }px, rgba(255,255,255,${intensity}), transparent 50%)`;

      // Remove previous shines
      const oldShine = card.querySelector(".dynamic-shine");
      if (oldShine) {
        card.removeChild(oldShine);
      }

      card.appendChild(shine);
    };

    const handleMouseLeave = () => {
      if (!tokenCardRef.current) return;
      const card = tokenCardRef.current;
      card.style.transform =
        "perspective(1000px) rotateX(0) rotateY(0) scale3d(1, 1, 1)";

      const shine = card.querySelector(".dynamic-shine");
      if (shine) {
        card.removeChild(shine);
      }
    };

    const card = tokenCardRef.current;
    if (card) {
      card.addEventListener("mousemove", handleMouseMove);
      card.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (card) {
        card.removeEventListener("mousemove", handleMouseMove);
        card.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, []);

  const formatSupply = (supply: string) => {
    if (!supply) return "0";
    const num = parseFloat(supply);
    if (num >= 1e12) return `${(num / 1e12).toFixed(1)}T`;
    if (num >= 1e9) return `${(num / 1e9).toFixed(1)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(1)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(1)}K`;
    return num.toLocaleString();
  };

  const generateBondingCurvePath = () => {
    const width = 300;
    const height = 120;
    const startX = 20;
    const startY = height - 20;
    const endX = width - 20;
    const endY = 20;

    // Create smooth bonding curve path
    const midX = width * 0.4;
    const midY = height * 0.6;
    const controlX = width * 0.7;
    const controlY = height * 0.2;

    return `M ${startX} ${startY} Q ${midX} ${midY} ${controlX} ${controlY} T ${endX} ${endY}`;
  };

  const getProgressPoint = (progress: number) => {
    const t = progress / 100;
    // Calculate point on the curve based on quadratic bezier approximation
    const x = 20 + t * 280;
    const y = 100 - (t * t * 80); // Simplified curve formula
    return { x, y };
  };

  const progressPoint = getProgressPoint(curveProgress);

  return (
    <div
      ref={tokenCardRef}
      className={`token-preview ${isHovered ? 'hovered' : ''}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="particles">
        <div className="particle particle-1"></div>
        <div className="particle particle-2"></div>
        <div className="particle particle-3"></div>
        <div className="particle particle-4"></div>
        <div className="particle particle-5"></div>
        <div className="particle particle-6"></div>
      </div>

      {/* Glow Effects */}
      <div
        className="glow-effect glow-1"
        style={{ transform: `rotate(${rotateAngle}deg)` }}
      ></div>
      <div className="glow-effect glow-2"></div>
      <div className="glow-effect glow-3"></div>

      {/* Coin Image */}
      <div className="coin-image-container">
        <div className="image-glow-ring" style={{ opacity: glowIntensity }}>
          <div className="image-glow-ring-inner">
            {previewImage ? (
              <img src={previewImage} alt="Token" className="coin-image" />
            ) : (
              <div className="placeholder-image">
                <div className="placeholder-icon">🪙</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Token Info */}
      <div className="token-info">
        <h2>{name || 'Your Coin'}</h2>
        <div className="token-symbol">{symbol || 'SYMBOL'}</div>
        <p>{animatedDescription}</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-label">Max Supply</div>
          <div className="stat-value animate-number">{formatSupply(maxSupply)}</div>
          <div className="stat-unit">Tokens</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Decimals</div>
          <div className="stat-value">{decimals}</div>
        </div>
        <div className="stat-item">
          <div className="stat-label">Fee</div>
          <div className="stat-value">{feeBps} bps</div>
        </div>
      </div>

      {/* Sandclock Animation - Form Completion Progress */}
      <div className="sandclock-section">
        <div className="sandclock-title">Form Completion</div>
        <SandclockAnimation progress={formCompletionProgress} />
        <div className="completion-status">
          {formCompletionProgress === 100 ? (
            <span className="status-complete">✓ Ready to Deploy</span>
          ) : (
            <span className="status-incomplete">
              Fill all fields to continue
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnhancedTokenPreview;